// "Cashflow Projection" - ported from reporting-hub/frontend/src/components/
// preflight/report/{CashflowProjectionShell,CashflowProjectionChart,
// CashflowProjectionPaymentsChart}.tsx. Cash balances per platform plus a
// short-term (12-week/3-"month") projection of scheduled CashFlows, fund-wide
// and per platform - one of the sub-views under the Reporting tab (see
// ReportingTab.js's own header comment: "the platform-scope switcher,
// Cashflow Projection, and IC Reporting sub-views are Phase D").
//
// The original renders two hand-rolled SVG stacked-bar charts (no library) -
// this port re-derives them as chart.js configs via primereact's <Chart>
// wrapper instead, following this repo's existing convention (see
// components/reporting/LendReportSections.js). One byte-for-byte thing NOT
// reproduced: the original draws a value label directly inside each bar
// segment plus a bold total label above each bar - chart.js has no built-in
// per-datapoint text layer (this repo has no chartjs-plugin-datalabels
// dependency, and none of the ~20 existing charts in LendReportSections.js/
// PlatformReport.js use one either) - exact figures are still fully
// available via the tooltip on hover, same tradeoff every other chart here
// already makes.
//
// Backend: GET/POST all proxied through exaport's own backend (port 5000,
// mounted at /api/compartment-report/..., see backend/api/compartment_report.js)
// which forwards to compartment_report_calc_service - never called directly.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { Chart } from 'primereact/chart';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { SelectButton } from 'primereact/selectbutton';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { useTheme } from '../../../hooks/useTheme';
import { applyChartJsTheme, applyChartColorsTheme, BASE_OPTIONS, chartRemountKey } from '../../reporting/chart_theme';
import { formatCurrency, formatDate, titleCasePlatform } from '../calculation/calcFormat';
import AxxionExportDialog from './AxxionExportDialog';
import { apiErrorDetail, runExport, useWizardJob } from '../jobs';
import errorMessage from '../../../helpers/errorMessage';

// Bottom-to-top stack order (kept distinct from calcFormat.js's own
// PLATFORM_ORDER, which is FINBEE-first and used for generic per-platform
// tables/pills elsewhere, not this chart's fixed stack). Series colours are
// the Exaloan brand palette (reporting/chart_theme.js: navy #0f1942, teal
// #077186, Riptide mint #9bd5c4) - a dark-to-light ramp for the platform
// stack, teal/mint for principal/interest - instead of the previous blue
// ramp and pink/orange pair, which clashed with the rest of the app. Navy is
// swapped for chart_theme.js's own dark-mode navy so it stays visible on the
// dark card. Colours only - the plotted values are unchanged.
const CASHFLOW_PLATFORM_ORDER = ['VAURAUS', 'MOZZENO', 'SAVY', 'FINBEE'];
const PLATFORM_SERIES_COLOR = {
  light: { VAURAUS: '#0f1942', MOZZENO: '#077186', SAVY: '#4d9f94', FINBEE: '#9bd5c4' },
  dark: { VAURAUS: '#7881a5', MOZZENO: '#0a95ad', SAVY: '#4d9f94', FINBEE: '#9bd5c4' },
};
const PAYMENT_COLORS = {
  light: { principal: '#077186', interest: '#9bd5c4' },
  dark: { principal: '#0a95ad', interest: '#9bd5c4' },
};
const paletteFor = (palette, theme) => palette[theme === 'dark' ? 'dark' : 'light'];

// Compact K/M axis-tick formatting - mirrors reporting-hub's
// formatCurrencyCompact() (format.ts), not otherwise ported into
// calcFormat.js since none of the existing (non-chart) consumers need it.
function formatCurrencyCompact(value) {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    const millions = abs / 1_000_000;
    return `${sign}${(millions >= 10 ? millions.toFixed(0) : millions.toFixed(1)).replace(/\.0$/, '')}M`;
  }
  if (abs >= 1_000) {
    const thousands = abs / 1_000;
    return `${sign}${(thousands >= 10 ? thousands.toFixed(0) : thousands.toFixed(1)).replace(/\.0$/, '')}K`;
  }
  return `${sign}${abs.toFixed(0)}`;
}

function platformShortLabel(platform) {
  return platform.charAt(0) + platform.slice(1).toLowerCase();
}

// ============================================================================
// "Projected Cash Balance" - stacked bar, index 0 = today's actual cash
// balance per platform (data.cash_balances), each subsequent point = that
// platform's running cumulative balance after adding the week's total
// (principal+interest). A platform whose cash balance isn't available
// (status !== "ok" or value === null) contributes 0 to every bar and is
// listed in the "Not included" footnote instead.
// ============================================================================
function CashBalanceChart({ data, scope }) {
  const { theme } = useTheme() || {};
  const points = useMemo(() => {
    const first = { axisLabel: 'Today', dateLabel: formatDate(data.report_date) };
    const weeks = data.fund.weeks.map((week, i) => ({
      axisLabel: `W${i + 1}`,
      dateLabel: formatDate(week.to),
    }));
    return [first, ...weeks];
  }, [data]);

  const series = useMemo(
    () =>
      CASHFLOW_PLATFORM_ORDER.map((platform) => {
        const balance = data.cash_balances.find((b) => b.platform === platform) || null;
        const included = !!balance && balance.status === 'ok' && balance.value !== null;
        const start = included ? balance.value : 0;
        const weeks = (data.by_platform[platform] && data.by_platform[platform].weeks) || [];
        const values = [start];
        let running = start;
        weeks.forEach((week) => {
          running += week.total;
          values.push(running);
        });
        return { platform, included, note: balance && balance.note, values };
      }),
    [data]
  );

  const scopedSeries = scope === 'FUND' ? series : series.filter((s) => s.platform === scope);
  const excludedSeries = scopedSeries.filter((s) => !s.included);

  const chartData = useMemo(
    () => ({
      labels: points.map((p) => p.axisLabel),
      datasets: scopedSeries.map((s) => ({
        label: platformShortLabel(s.platform),
        backgroundColor: paletteFor(PLATFORM_SERIES_COLOR, theme)[s.platform],
        data: s.values,
        stack: 'cash',
      })),
    }),
    [points, scopedSeries, theme]
  );

  const options = useMemo(
    () => ({
      ...BASE_OPTIONS,
      scales: { x: { stacked: true }, y: { stacked: true, ticks: { callback: (v) => formatCurrencyCompact(v) } } },
      plugins: {
        legend: { position: 'bottom', labels: { boxWidth: 12 } },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (items) => {
              const idx = items[0]?.dataIndex ?? 0;
              const p = points[idx];
              return p ? `${p.axisLabel === 'Today' ? 'Today' : p.axisLabel} · ${p.dateLabel}` : '';
            },
            label: (item) => `${item.dataset.label}: ${formatCurrency(item.raw)}`,
            footer: (items) => `Total cash: ${formatCurrency(items.reduce((sum, i) => sum + (i.raw || 0), 0))}`,
          },
        },
      },
    }),
    [points]
  );

  return (
    <div>
      <h5 className="cr-subsection-title mb-1">Projected Cash Balance</h5>
      <p className="cr-card-subtitle mb-2">
        Cumulative cash by Originator - 12-week outlook from {formatDate(data.report_date)}
      </p>
      <div style={{ height: 340 }}>
        <Chart key={chartRemountKey(chartData)} type="bar" data={chartData} options={options} style={{ height: '100%', width: '100%' }} />
      </div>
      {excludedSeries.length > 0 && (
        <p className="text-color-secondary text-sm mt-2 mb-0">
          Not included (cash balance unavailable): {excludedSeries.map((s) => platformShortLabel(s.platform)).join(', ')}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// "Weekly Cashflow Payments" - stacked Principal(bottom)+Interest(top) bar,
// 12 points (no "Today" - every bar is a future scheduled inflow).
// ============================================================================
function PaymentsChart({ data, scope }) {
  const { theme } = useTheme() || {};
  const seriesData = scope === 'FUND' ? data.fund : data.by_platform[scope];
  const weeks = seriesData.weeks;

  const chartData = useMemo(
    () => ({
      labels: weeks.map((_, i) => `W${i + 1}`),
      datasets: [
        { label: 'Principal Payments', backgroundColor: paletteFor(PAYMENT_COLORS, theme).principal, data: weeks.map((w) => w.principal), stack: 'payments' },
        { label: 'Interest Payments', backgroundColor: paletteFor(PAYMENT_COLORS, theme).interest, data: weeks.map((w) => w.interest), stack: 'payments' },
      ],
    }),
    [weeks, theme]
  );

  const options = useMemo(
    () => ({
      ...BASE_OPTIONS,
      scales: { x: { stacked: true }, y: { stacked: true, ticks: { callback: (v) => formatCurrencyCompact(v) } } },
      plugins: {
        legend: { position: 'bottom', labels: { boxWidth: 12 } },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (items) => {
              const idx = items[0]?.dataIndex ?? 0;
              const w = weeks[idx];
              return w ? `${w.label} · ${formatDate(w.to)}` : '';
            },
            label: (item) => `${item.dataset.label}: ${formatCurrency(item.raw)}`,
            footer: (items) => `Total payments: ${formatCurrency(items.reduce((sum, i) => sum + (i.raw || 0), 0))}`,
          },
        },
      },
    }),
    [weeks]
  );

  return (
    <div>
      <h5 className="cr-subsection-title mb-1">Weekly Cashflow Payments</h5>
      <p className="cr-card-subtitle mb-2">
        Scheduled principal &amp; interest - 12-week outlook from {formatDate(data.report_date)}
      </p>
      <div style={{ height: 340 }}>
        <Chart key={chartRemountKey(chartData)} type="bar" data={chartData} options={options} style={{ height: '100%', width: '100%' }} />
      </div>
    </div>
  );
}

// ============================================================================
// Tables - Week1..N interspersed with a bold "Month N" subtotal row every 4
// weeks (rolling 4-week blocks, not calendar months). Shared by the
// "Overview" tab (fund-wide) and the "By Platform (Detail)" tab.
// ============================================================================
function WeekMonthTable({ series }) {
  const weeksPerMonth = series.months.length ? series.weeks.length / series.months.length : series.weeks.length;
  const rows = useMemo(() => {
    const out = [];
    series.months.forEach((month, monthIdx) => {
      series.weeks.slice(monthIdx * weeksPerMonth, (monthIdx + 1) * weeksPerMonth).forEach((week) => out.push({ ...week, _bold: false }));
      out.push({ ...month, _bold: true });
    });
    return out;
  }, [series, weeksPerMonth]);

  return (
    <DataTable value={rows} size="small" showGridlines rowClassName={(row) => ({ 'cr-subtotal-row': row._bold })}>
      <Column field="label" header="Week" />
      <Column field="from" header="From" body={(row) => formatDate(row.from)} />
      <Column field="to" header="To" body={(row) => formatDate(row.to)} />
      <Column field="principal" header="Principal Payments" align="right" headerClassName="cr-num" bodyClassName="cr-num" body={(row) => formatCurrency(row.principal)} />
      <Column field="interest" header="Interest Payments" align="right" headerClassName="cr-num" bodyClassName="cr-num" body={(row) => formatCurrency(row.interest)} />
      <Column field="total" header="Total Payments" align="right" headerClassName="cr-num" bodyClassName="cr-num" body={(row) => formatCurrency(row.total)} />
    </DataTable>
  );
}

// "<Metric> Payments by Platform" pivot - one column per platform plus a
// Total column (= client-side sum of the 4 shown platform cells, NOT
// guaranteed equal to the fund-wide table's own total - same intentional
// discrepancy as the original, see cashflow_projection.py).
function PlatformPivotTable({ title, byPlatform, metric }) {
  const sample = byPlatform[CASHFLOW_PLATFORM_ORDER[0]];
  const weeksPerMonth = sample.months.length ? sample.weeks.length / sample.months.length : sample.weeks.length;

  const rows = useMemo(() => {
    const out = [];
    sample.months.forEach((month, monthIdx) => {
      for (let offset = 0; offset < weeksPerMonth; offset++) {
        const weekIdx = monthIdx * weeksPerMonth + offset;
        const label = sample.weeks[weekIdx].label;
        const values = {};
        CASHFLOW_PLATFORM_ORDER.forEach((p) => {
          values[p] = byPlatform[p].weeks[weekIdx][metric];
        });
        out.push({ label, ...values, _bold: false });
      }
      const values = {};
      CASHFLOW_PLATFORM_ORDER.forEach((p) => {
        values[p] = byPlatform[p].months[monthIdx][metric];
      });
      out.push({ label: month.label, ...values, _bold: true });
    });
    return out;
  }, [sample, byPlatform, metric, weeksPerMonth]);

  const totalBody = (row) => formatCurrency(CASHFLOW_PLATFORM_ORDER.reduce((sum, p) => sum + row[p], 0));

  return (
    <div className="mb-4">
      <h5 className="cr-subsection-title">{title}</h5>
      <DataTable value={rows} size="small" showGridlines rowClassName={(row) => ({ 'cr-subtotal-row': row._bold })}>
        <Column field="label" header="" />
        {CASHFLOW_PLATFORM_ORDER.map((p) => (
          <Column key={p} field={p} header={titleCasePlatform(p)} align="right" headerClassName="cr-num" bodyClassName="cr-num" body={(row) => formatCurrency(row[p])} />
        ))}
        <Column header="Total Payments" align="right" headerClassName="cr-num" bodyClassName="cr-num" body={totalBody} />
      </DataTable>
    </div>
  );
}

// ============================================================================
// Shell
// ============================================================================
const CashflowProjectionShell = ({ reportingId, setLoading }) => {
  const { theme } = useTheme() || {};
  const [data, setData] = useState(null);
  const [loading, setLocalLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [axxionOpen, setAxxionOpen] = useState(false);
  const [detailPlatform, setDetailPlatform] = useState(CASHFLOW_PLATFORM_ORDER[0]);
  const [chartScope, setChartScope] = useState('FUND');
  const menuRef = useRef(null);
  const toast = useRef(null);
  const { runJob, busy } = useWizardJob();

  useEffect(() => {
    applyChartJsTheme(theme);
    applyChartColorsTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (!reportingId) return;
    let cancelled = false;
    setLocalLoading(true);
    if (setLoading) setLoading(true);
    axiosWithAuth
      .get(`/compartment-report/reportings/${reportingId}/cashflow-projection`)
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
          setError(null);
        }
      })
      .catch((err) => {
        // 404 until "Confirm & Calculate" has run for this reporting - not
        // a real error, the tab just isn't reachable that early normally.
        if (!cancelled) {
          setData(null);
          setError(err?.response?.status === 404 ? null : errorMessage(err, 'Failed to load Cashflow Projection.'));
          if (err?.response?.status !== 404) console.error('[CashflowProjectionShell] fetch error:', errorMessage(err));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLocalLoading(false);
          if (setLoading) setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reportingId, setLoading]);

  const handleExport = async (variant, cutoffDate) => {
    setExporting(true);
    try {
      const params = { kind: 'cashflow_projection', variant };
      if (cutoffDate) params.cutoff_date = cutoffDate;
      await runExport(runJob, reportingId, params);
    } catch (err) {
      if (err.cancelled) return;
      console.error('[CashflowProjectionShell] export error:', err.detail || errorMessage(err));
      toast.current?.show({
        severity: 'error',
        summary: 'Excel export failed',
        detail: apiErrorDetail(err, 'Excel export failed.'),
        sticky: true,
      });
    }
    setExporting(false);
  };

  const exportMenuItems = [
    { label: 'Static', command: () => handleExport('static') },
    { label: 'With Formulas (+ CashFlows sheet)', command: () => handleExport('formulas') },
    { label: 'Axxion Export', command: () => setAxxionOpen(true) },
  ];

  if (error) {
    return <div className="ex-inline-error">{error}</div>;
  }
  // No calculation result yet for this reporting (404) - not an error to
  // surface, the Reporting tab normally isn't reachable before "Confirm &
  // Calculate" has run.
  if (!loading && !data) {
    return <p className="text-color-secondary">Cashflow Projection isn't available yet - run "Confirm &amp; Calculate" first.</p>;
  }

  return (
    <div className="cr-card cr-report-card">
      <Toast ref={toast} />
      <div className="flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h4 className="cr-card-title">Cashflow Projection</h4>
        <div className="flex align-items-center gap-2">
          <Menu model={exportMenuItems} popup ref={menuRef} />
          <Button
            label={exporting ? 'Exporting…' : 'Export Excel'}
            icon={exporting ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
            className="p-button-outlined p-button-sm"
            disabled={exporting || busy || loading || !data}
            onClick={(e) => menuRef.current.toggle(e)}
          />
          {data && (
            <AxxionExportDialog
              reportingId={reportingId}
              visible={axxionOpen}
              onHide={() => setAxxionOpen(false)}
              defaultCutoffDate={data.report_date}
              onExport={(cutoffDate) => handleExport('axxion', cutoffDate)}
            />
          )}
        </div>
      </div>

      {loading || !data ? (
        <Skeleton height="400px" borderRadius="12px" />
      ) : (
        <div>
          <SelectButton
            value={chartScope}
            options={['FUND', ...CASHFLOW_PLATFORM_ORDER].map((s) => ({ label: s === 'FUND' ? 'Fund' : titleCasePlatform(s), value: s }))}
            onChange={(e) => (e.value ? setChartScope(e.value) : null)}
            className="mb-3"
          />

          <div className="grid">
            <div className="col-12 lg:col-6">
              <CashBalanceChart data={data} scope={chartScope} />
            </div>
            <div className="col-12 lg:col-6">
              <PaymentsChart data={data} scope={chartScope} />
            </div>
          </div>

          <TabView className="mt-3">
            <TabPanel header="Overview">
              <h5 className="cr-subsection-title">Weekly Cashflow Projection</h5>
              <WeekMonthTable series={data.fund} />
            </TabPanel>
            <TabPanel header="By Originator (Summary)">
              <PlatformPivotTable title="Total Payments" byPlatform={data.by_platform} metric="total" />
              <PlatformPivotTable title="Principal Payments" byPlatform={data.by_platform} metric="principal" />
              <PlatformPivotTable title="Interest Payments" byPlatform={data.by_platform} metric="interest" />
            </TabPanel>
            <TabPanel header="By Originator (Detail)">
              <SelectButton
                value={detailPlatform}
                options={CASHFLOW_PLATFORM_ORDER.map((p) => ({ label: titleCasePlatform(p), value: p }))}
                onChange={(e) => (e.value ? setDetailPlatform(e.value) : null)}
                className="mb-3"
              />
              <WeekMonthTable series={data.by_platform[detailPlatform]} />
            </TabPanel>
          </TabView>
        </div>
      )}
    </div>
  );
};

export default CashflowProjectionShell;
